import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { FrosterCalculatorBases } from "@/components/FrosterStructureTable";
import CalculatorFlavorSelect from "@/components/CalculatorFlavorSelect";
import CalculatorHeader from "@/components/CalculatorHeader";
import { PRODUCTION_DRIVER_PAGE_CSS } from "@/lib/productionDriverPageStyles";
import { fetchCalculatorFlavors, fetchFrosterCalculator } from "@/lib/calculatorApi";

const ORDER_TYPES = [
  { value: "Custom", label: "Custom" },
  { value: "RTG", label: "RTG" },
];

export default function FrosterCalculator({ embedded = false }) {
  const [flavors, setFlavors] = useState([]);
  const [flavor, setFlavor] = useState("");
  const [orderType, setOrderType] = useState("Custom");
  const [structureBlock, setStructureBlock] = useState(null);
  const [loadingFlavors, setLoadingFlavors] = useState(true);
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingFlavors(true);
        const list = await fetchCalculatorFlavors();
        if (cancelled) return;
        setFlavors(list);
        if (list.length > 0) {
          setFlavor((prev) => prev || list[0].flavor);
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || String(e));
      } finally {
        if (!cancelled) setLoadingFlavors(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!flavor) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingStructure(true);
        setErr("");
        const data = await fetchFrosterCalculator({
          flavor,
          weightKg: 0.5,
          orderType,
          tiers: 0,
        });
        if (!cancelled) {
          const block = data?.frosting_structures?.[0] || null;
          setStructureBlock(block);
          if (data?.message && !block) {
            setErr(data.message);
          } else {
            setErr("");
          }
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e.message || String(e));
          setStructureBlock(null);
        }
      } finally {
        if (!cancelled) setLoadingStructure(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [flavor, orderType]);

  const basesKey = `${flavor}-${orderType}`;

  return (
    <div
      className={`driver-page${embedded ? " driver-page--embedded" : ""}`}
      style={
        embedded
          ? { minHeight: 0, padding: "0 0 16px", maxWidth: "none", margin: 0 }
          : undefined
      }
    >
      <style>{PRODUCTION_DRIVER_PAGE_CSS}</style>
      {embedded ? (
        <style>{`
          .driver-page--embedded .driver-card:first-of-type { margin-top: 0; }
        `}</style>
      ) : null}

      {!embedded ? (
        <CalculatorHeader
          title="Froster calculator"
          otherLabel="Baker →"
          otherTo="/calculator/baker"
        />
      ) : null}

      <div className="driver-card">
        <div className="driver-label">Flavor</div>
        <CalculatorFlavorSelect
          flavors={flavors}
          value={flavor}
          onChange={setFlavor}
          loading={loadingFlavors}
        />

        <div className="driver-label mt-4">Frost type</div>
        <div className="driver-frost-line">
          {ORDER_TYPES.map((ot) => (
            <button
              key={ot.value}
              type="button"
              onClick={() => setOrderType(ot.value)}
              className={`driver-frost-tag ${
                ot.value === "RTG" ? "driver-frost-tag--rtg" : "driver-frost-tag--custom"
              }`}
              style={orderType === ot.value ? undefined : { opacity: 0.45 }}
            >
              {ot.label}
            </button>
          ))}
        </div>

        {flavor ? (
          <div className="driver-frost-line mt-3">
            <span className="driver-frost-chip driver-frost-chip--flavor">{flavor}</span>
            <span
              className={`driver-frost-tag ${
                orderType === "RTG" ? "driver-frost-tag--rtg" : "driver-frost-tag--custom"
              }`}
            >
              {orderType}
            </span>
          </div>
        ) : null}
      </div>

      {err ? <div className="driver-error">{err}</div> : null}

      {loadingStructure && !structureBlock ? (
        <div className="driver-card flex items-center justify-center gap-2 py-8 text-sm text-[#666]">
          <Loader2 className="h-5 w-5 animate-spin text-[#EE2677]" />
          Loading structure…
        </div>
      ) : null}

      {structureBlock ? (
        <FrosterCalculatorBases key={basesKey} block={structureBlock} />
      ) : null}
    </div>
  );
}
